import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Description,
  PictureAsPdf,
  TableChart,
  BarChart,
  Timeline,
  People,
  AttachMoney,
  Receipt,
  Assessment,
} from '@mui/icons-material';

// ReportItem structure handled with JS

export const ReportsCenter = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const reports: ReportItem[] = [
    {
      title: 'Member Summary Report',
      description: 'Overview of all members with their status and dues',
      icon: <People color="primary" />,
      category: 'members',
    },
    {
      title: 'Financial Statement',
      description: 'Income statement and balance sheet',
      icon: <BarChart color="primary" />,
      category: 'financial',
    },
    {
      title: 'Dues Collection Report',
      description: 'Track dues payments and outstanding amounts',
      icon: <AttachMoney color="primary" />,
      category: 'dues',
    },
    {
      title: 'Expense Analysis',
      description: 'Detailed breakdown of expenses by category',
      icon: <Receipt color="primary" />,
      category: 'financial',
    },
    {
      title: 'Budget Performance',
      description: 'Compare actual spending against budget',
      icon: <Assessment color="primary" />,
      category: 'budget',
    },
    {
      title: 'Annual Report',
      description: 'Comprehensive yearly overview',
      icon: <Timeline color="primary" />,
      category: 'annual',
    },
  ];

  const filteredReports = selectedCategory === 'all' 
    ? reports 
    : reports.filter(r => r.category === selectedCategory);

  const handleGenerateReport = (reportTitle: string) => {
    // Placeholder for report generation
    console.log(`Generating ${reportTitle}...`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Reports</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="all">All Reports</MenuItem>
            <MenuItem value="members">Member Reports</MenuItem>
            <MenuItem value="financial">Financial Reports</MenuItem>
            <MenuItem value="dues">Dues Reports</MenuItem>
            <MenuItem value="budget">Budget Reports</MenuItem>
            <MenuItem value="annual">Annual Reports</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Reports Generated
              </Typography>
              <Typography variant="h4">0</Typography>
              <Typography variant="body2" color="textSecondary">
                This month
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Scheduled Reports
              </Typography>
              <Typography variant="h4">0</Typography>
              <Typography variant="body2" color="textSecondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Last Generated
              </Typography>
              <Typography variant="h6">-</Typography>
              <Typography variant="body2" color="textSecondary">
                No reports yet
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Export Formats
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <PictureAsPdf />
                <TableChart />
                <Description />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Available Reports */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Available Reports
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {filteredReports.length > 0 ? (
            filteredReports.map((report, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>{report.icon}</ListItemIcon>
                  <ListItemText
                    primary={report.title}
                    secondary={report.description}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleGenerateReport(report.title)}
                      disabled
                    >
                      Generate
                    </Button>
                    <Button size="small" disabled>
                      Schedule
                    </Button>
                  </Box>
                </ListItem>
                {index < filteredReports.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))
          ) : (
            <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
              Report generation will be available soon
            </Typography>
          )}
        </List>
      </Paper>
    </Box>
  );
};
